import { isDefined } from '@sgme/fp';
import { isEqual } from 'lodash-es';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';

import type { DataById } from '../formData.model';
import { getSubKeyExtractor } from './extractors';
import { getBatchParentFieldId } from './patchedFields.batch.helper';

const PATCHABLE_HIDDEN_FIELD_IDS = new Set(['InitialValuationDate', 'StrikeMode']);

export function getPatchedFieldClassName(pulseParity: Readonly<Record<string, number>>, key: string): string {
  const pulse = (pulseParity[key] ?? 0) % 2 === 0 ? 'even' : 'odd';
  return `is-modified-field is-modified-field-pulse-${pulse}`;
}

export function computePatchedFieldsDiff(
  previous: DataById,
  current: DataById,
  fieldsMetadata: FieldMetadataById,
): readonly string[] {
  const candidates = new Set<string>([...Object.keys(previous), ...Object.keys(current)]);
  const result: string[] = [];
  for (const id of candidates) {
    const meta = fieldsMetadata[id];

    // These fields stay hidden in metadata but are rendered in the Underlyings
    // strike header, so patched-field diffing must still expose them.
    if (!meta || (meta.isHidden && !PATCHABLE_HIDDEN_FIELD_IDS.has(id))) {
      continue;
    }
    if (!isEqual(previous[id], current[id])) {
      const extractor = getSubKeyExtractor(meta);
      if (extractor) {
        result.push(...extractor(id, meta, previous[id], current[id]));
      } else {
        result.push(id);
      }

      const batchParentFieldId = getBatchParentFieldId(id, fieldsMetadata);
      if (isDefined(batchParentFieldId)) {
        result.push(batchParentFieldId);
      }
    }
  }
  return result;
}
